import { useEffect, useState } from "react";
import MultiTable from "../../../Common/MUITable";
import TripToFctsFilter from "./TripToFctsFilter";
import DelhiReportExport from "./DelhiReportExport";
import { GetVehicleTripSummary } from "./ReportApi";
import { getTodayEndDateTime, getTodayStartDateTime } from "./reportCommon";

const DEFAULT_TRIP_TO_FCTS_FILTERS = {
  fctsId: "",
  vehicleType: "",
  fromDateTime: getTodayStartDateTime(),
  toDateTime: getTodayEndDateTime(),
};

const mapApiRowToTableRow = (row) => ({
  fctsName: row["FCTS Name"],
  isTotalRow: row["FCTS Name"] === "Total",
  uniqueVehicles: row["Unique Vehicles"] ?? 0,
  totalReported: row["Total Reported"] ?? 0,
  trips1: row["Trips 1"] ?? 0,
  trips2: row["Trips 2"] ?? 0,
  trips3: row["Trips 3"] ?? 0,
  moreThan4: row["More than 4"] ?? 0,
  percentBasedOnTotalReported: row["Percentage"] ?? 0,
});

const mapFiltersToApiPayload = (filters) => ({
  fromDate: filters.fromDateTime,
  toDate: filters.toDateTime,
  vehicleTypeId: filters.vehicleType,
  transferStationId: filters.fctsId,
});

const totalCellStyle = { fontWeight: 700 };

const renderTotalAwareCell = (field) => (row) =>
  row.isTotalRow ? (
    <span style={totalCellStyle}>{row[field]}</span>
  ) : (
    row[field]
  );

const exportColumns = [
  { label: "FCTS Name", field: "fctsName" },
  { label: "Unique vehicles", field: "uniqueVehicles" },
  { label: "Total reported", field: "totalReported" },
  { label: "Trips 1", field: "trips1" },
  { label: "Trips 2", field: "trips2" },
  { label: "Trips 3", field: "trips3" },
  { label: "More than 4", field: "moreThan4" },
  { label: "% based on total reported", field: "percentBasedOnTotalReported" },
];

const TripToFctsReport = () => {
  const [filters, setFilters] = useState(DEFAULT_TRIP_TO_FCTS_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(
    DEFAULT_TRIP_TO_FCTS_FILTERS
  );
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await GetVehicleTripSummary(mapFiltersToApiPayload(values));
      if (response?.data?.success) {
        const rows = (response.data.data || []).map(mapApiRowToTableRow);
        setFilteredRows(rows);
      } else {
        setError(
          response?.data?.message ||
            response?.message ||
            "Failed to fetch trip to FCTS summary."
        );
        setFilteredRows([]);
      }
    } catch (err) {
      setError(err?.message || "Something went wrong.");
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const handleSearch = (values) => {
    setAppliedFilters(values);
  };

  const handleReset = () => {
    setFilters(DEFAULT_TRIP_TO_FCTS_FILTERS);
    setAppliedFilters(DEFAULT_TRIP_TO_FCTS_FILTERS);
  };

  const tableColumns = [
    {
      label: "FCTS Name",
      field: "fctsName",
      width: "180px",
      render: renderTotalAwareCell("fctsName"),
    },
    {
      label: "Unique vehicles",
      field: "uniqueVehicles",
      width: "140px",
      render: renderTotalAwareCell("uniqueVehicles"),
    },
    {
      label: "Total reported",
      field: "totalReported",
      width: "130px",
      render: renderTotalAwareCell("totalReported"),
    },
    {
      label: "Trips 1",
      field: "trips1",
      width: "100px",
      render: renderTotalAwareCell("trips1"),
    },
    {
      label: "Trips 2",
      field: "trips2",
      width: "100px",
      render: renderTotalAwareCell("trips2"),
    },
    {
      label: "Trips 3",
      field: "trips3",
      width: "100px",
      render: renderTotalAwareCell("trips3"),
    },
    {
      label: "More than 4",
      field: "moreThan4",
      width: "120px",
      render: renderTotalAwareCell("moreThan4"),
    },
    {
      label: "% based on total reported",
      field: "percentBasedOnTotalReported",
      width: "180px",
      render: renderTotalAwareCell("percentBasedOnTotalReported"),
    },
  ];

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">Trip to FCTS</h5>
            <p className="text-muted mb-0">View trip to FCTS report.</p>
          </div>
          <DelhiReportExport
            reportData={filteredRows}
            reportTitle="Trip to FCTS"
            columns={exportColumns}
            fileName="TripToFctsReport"
          />
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
            <TripToFctsFilter
              initialValues={filters}
              onSearch={(values) => {
                setFilters(values);
                handleSearch(values);
              }}
              onReset={handleReset}
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="top-header text-center">
              <span>Trip to FCTS</span>
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={filteredRows}
                loading={loading}
                tableHeight={500}
                hasTotalRow
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripToFctsReport;